import { shownTime } from './round-config'

// Parking hours read from a customer's Parking words (Liam, 22 Oct 2026: "if there's a note for it, then a suggestion comes
// up when we try and drag it into that time or book them into that time. Because although we could override it, we
// really would want to know"; docs/plans/job-bits/PLAN.md 1). The words stay as typed (customers.parking_restrictions,
// imported or added on Today); only one clear range of hours is read from them, and nothing is stored. Part of Parking
// notes (business-settings.ts parkingNotesOn), off as standard. Pure, so the checks run it without a page.

export interface ParkingHours { from: string; to: string }

// "11-1", "11am-1pm", "11 to 1", "between 11 and 1", "11:00-13:00", "11.30-1.30". Not prices, lengths, counts or dates
// (a date imported into the box, "2025-01-11 00:00:00", was read as 1pm to 11pm before this: seen on the live round).
const RANGE = /(^|[^\d£$:.\-/])(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?\s*-\s*(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?(?![\d:.])(?![-/]\d)(?!\s*(?:hours?|hrs?|mins?|minutes?|days?|weeks?|times?|x\b|%|p\b))/g
// One end only: "Bus lane active till 10" (a note on the live round) is before 10am.
const UNTIL = /\b(?:till|til|until|before)\s+(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?(?![\d:.\-/])/

const BEFORE_HOUSE = /\b(?:flats?|no|nos|numbers?|houses?|units?|apartments?|apt|#)\.?\s*$/
const AFTER_HOUSE = /^\s*(?:[a-z]+\s+)?(?:road|rd|street|st|avenue|ave|lane|ln|close|way|drive|dr|court|ct|crescent|gardens|gdns|place|pl|mews|terrace|grove|hill|park|row|square|sq)\b/

const pad = (n: number): string => String(n).padStart(2, '0')

function hourOf(h: number, half: string | undefined): number | null {
  if (half === 'am') return h === 12 ? 0 : h <= 12 ? h : null
  if (half === 'pm') return h === 12 ? 12 : h <= 12 ? h + 12 : null
  return h
}

// Words that turn the hours round, or leave them unclear (GPT-6.1: "Parking available 11-1" means the opposite).
const ALLOWED = /\b(?:available|allowed|free parking|ok to park|fine to park|can (?:only )?park|park(?:ing)? (?:only|ok|fine))\b/

export function parkingHours(text: string | null | undefined): ParkingHours | null {
  if (!text || ALLOWED.test(text.toLowerCase())) return null
  const words = text.toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\b(?:noon|midday)\b/g, '12pm')
    .replace(/\bbetween\s+(\S+)\s+and\s+(\S+)/g, '$1-$2')
    .replace(/\s+(?:to|till|until|til)\s+/g, '-')
  const found = new Map<string, ParkingHours>()
  for (const m of words.matchAll(RANGE)) {
    const [, , h1s, m1s, a1, h2s, m2s, a2] = m
    // House and flat numbers are not hours (Sonnet's review): "11-13 Oak Rd", "Flat 2-4", "Nos 5-7".
    const at = (m.index ?? 0) + m[1]!.length
    if (BEFORE_HOUSE.test(words.slice(Math.max(0, at - 12), at)) || AFTER_HOUSE.test(words.slice(at + m[0].length - m[1]!.length))) continue
    let h1 = Number(h1s), h2 = Number(h2s)
    const m1 = m1s ? Number(m1s) : 0, m2 = m2s ? Number(m2s) : 0
    if (h1 > 23 || h2 > 23 || m1 > 59 || m2 > 59) continue
    // One am/pm said for both ("11-1pm" is 11am to 1pm; "2-4pm" is 2pm to 4pm).
    let half1 = a1, half2 = a2
    if (!half1 && half2) half1 = half2 === 'pm' && h1 <= 12 && (hourOf(h1, 'pm') ?? 99) > (hourOf(h2, 'pm') ?? 0) ? 'am' : half2
    if (!half2 && half1) half2 = half1
    const f = hourOf(h1, half1), t = hourOf(h2, half2)
    if (f === null || t === null) continue
    h1 = f; h2 = t
    // No am or pm, both on a 12-hour clock: a window cleaner's day. 1 to 6 are the afternoon; a later end that reads
    // smaller is the afternoon too ("11-1" is 11am to 1pm).
    if (!a1 && !a2 && h1 <= 12 && h2 <= 12) {
      if (h1 >= 1 && h1 <= 6) h1 += 12
      if (h2 >= 1 && h2 <= 6 || h2 < h1) h2 += 12
    }
    if (h2 > 23 || h1 * 60 + m1 >= h2 * 60 + m2) continue
    const r = { from: `${pad(h1)}:${pad(m1)}`, to: `${pad(h2)}:${pad(m2)}` }
    found.set(`${r.from}-${r.to}`, r)
  }
  // One clear range, or nothing: two different ranges are left to the words.
  if (found.size) return found.size === 1 ? [...found.values()][0]! : null
  const u = UNTIL.exec(text.toLowerCase())
  if (!u) return null
  let h = hourOf(Number(u[1]), u[3])
  const m = u[2] ? Number(u[2]) : 0
  if (h === null || h > 23 || m > 59) return null
  if (!u[3] && h >= 1 && h <= 6) h += 12
  return h * 60 + m > 0 ? { from: '00:00', to: `${pad(h)}:${pad(m)}` } : null
}

// "11am to 1pm", or "before 10am", the round's way.
export function parkingWords(h: ParkingHours): string {
  return h.from === '00:00' ? `before ${shownTime(h.to)}` : `${shownTime(h.from)} to ${shownTime(h.to)}`
}

const minutesOf = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))

// A visit's time ("10:30", or the start of "09:00-11:30") falls in the hours, or with a known length runs into them.
export function inParkingHours(slot: string | null | undefined, h: ParkingHours | null, lengthMinutes: number | null = null): boolean {
  if (!h || !slot) return false
  const m = /^\s*(\d{1,2}):(\d{2})/.exec(slot)
  if (!m) return false
  const start = Number(m[1]) * 60 + Number(m[2])
  const from = minutesOf(h.from), to = minutesOf(h.to)
  if (start >= from && start < to) return true
  return lengthMinutes !== null && lengthMinutes > 0 && start < from && start + lengthMinutes > from
}
